import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { KpiCards } from "@/components/console/Kpis";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fcfa, fdate, fdatetime, fphone } from "@/lib/format";
import { methodLabel, purposeLabel, roleLabel, statusInfo } from "@/lib/i18n";
import { DataView } from "@/components/console/DataView";
import { useAuth, can } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, Loader, PageTitle, StatusBadge } from "@/components/common";
import { ExportButton } from "@/pages/console/Phase1C";

const ROLES = ["membre", "moderateur", "tresorier", "admin", "super_admin"];
const STATUSES = ["candidat", "actif", "suspendu", "radie"];

function EditMember({ m, onClose, onSaved }) {
  const { me } = useAuth();
  const [status, setStatus] = useState(m.status);
  const [role, setRole] = useState(m.global_role);
  const [reason, setReason] = useState("");
  const [discount, setDiscount] = useState("");
  const [error, setError] = useState("");
  const save = async () => {
    setError("");
    try {
      const body = { reason };
      if (status !== m.status) body.status = status;
      if (role !== m.global_role) body.global_role = role;
      await api.patch(`/admin/members/${m.id}`, body);
      toast.success("Membre mis à jour");
      onSaved();
    } catch (e) {
      setError(errMsg(e));
    }
  };
  const adjust = async (kind) => {
    setError("");
    try {
      const amount = kind === "remise" ? parseInt(discount || "0", 10) : 0;
      if (kind === "remise" && !(amount > 0)) return setError("Indiquez le montant de la remise.");
      const { data } = await api.post(`/admin/members/${m.id}/membership-adjustment`, { kind, amount, reason });
      toast.success(`Cotisation ajustée : ${data.amount} FCFA`);
      onSaved();
    } catch (e) {
      setError(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-mr-surface border-mr-border" data-testid="member-edit-dialog">
        <DialogHeader><DialogTitle className="font-display">{m.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <label className="block"><span className="mr-label">Statut</span>
            <select data-testid="member-edit-status-select" className="mr-input" value={status} onChange={(e) => setStatus(e.target.value)}>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
          </label>
          {can(me, "roles:manage") && (
            <label className="block"><span className="mr-label">Rôle global</span>
              <select data-testid="member-edit-role-select" className="mr-input" value={role} onChange={(e) => setRole(e.target.value)}>{ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}</select>
            </label>
          )}
          <label className="block"><span className="mr-label">Motif (journalisé)</span><input data-testid="member-edit-reason-input" className="mr-input" value={reason} onChange={(e) => setReason(e.target.value)} /></label>
          {error && <p className="text-sm text-mr-danger" data-testid="member-edit-error">{error}</p>}
          <button data-testid="member-edit-save-button" className="mr-btn-primary w-full" onClick={save}>Enregistrer</button>
          <input data-testid="member-discount-amount-input" className="mr-input" inputMode="numeric" placeholder="Montant de la remise (FCFA)" value={discount} onChange={(e) => setDiscount(e.target.value.replace(/\D/g, ""))} />
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="member-exempt-button" className="mr-btn-secondary" onClick={() => adjust("exoneration")} disabled={reason.length < 3}>Exonérer</button>
            <button data-testid="member-discount-button" className="mr-btn-secondary" onClick={() => adjust("remise")} disabled={reason.length < 3}>Accorder une remise</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const MEMBER_TABS = [["", "Tous"], ["actif", "Actifs"], ["suspendu", "Suspendus"], ["candidat", "Candidats"], ["radie", "Radiés"], ["liste_noire", "Liste noire"]];

function NewMember({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", phone: "", email: "", company: "", city: "", sectors: "", global_role: "membre", status: "actif" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/members", { ...f, sectors: f.sectors.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Membre créé : un e-mail l'invite à choisir son code");
      onSaved(data.id);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const inp = (k, l, t = "text") => <label key={k}><span className="mr-label">{l}</span><input type={t} data-testid={`new-member-${k}`} className="mr-input" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></label>;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="new-member-form">
        <DialogHeader><DialogTitle>Nouveau membre</DialogTitle></DialogHeader>
        {inp("name", "Nom complet")}{inp("phone", "Téléphone", "tel")}{inp("email", "E-mail", "email")}{inp("company", "Entreprise")}{inp("city", "Ville / commune")}{inp("sectors", "Secteurs (séparés par des virgules)")}
        <label><span className="mr-label">Statut</span><select data-testid="new-member-status" className="mr-input" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}><option value="actif">Actif (cotisation réglée hors application)</option><option value="candidat">Candidat (cotisation à payer)</option></select></label>
        <label><span className="mr-label">Rôle</span><select data-testid="new-member-role" className="mr-input" value={f.global_role} onChange={(e) => setF({ ...f, global_role: e.target.value })}>{["membre", "moderateur", "tresorier", "admin"].map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}</select></label>
        <button data-testid="new-member-save" className="mr-btn-primary" disabled={busy || f.name.length < 3 || f.phone.length < 8 || !f.email.includes("@")} onClick={save}>{busy ? "Création…" : "Créer le membre"}</button>
      </DialogContent>
    </Dialog>
  );
}

const opts = (keys, fn = (k) => statusInfo(k)[0]) => keys.map((k) => [k, fn(k)]);

function BulkStatusDialog({ members, onClose, onDone }) {
  const [status, setStatus] = useState("suspendu");
  const [reason, setReason] = useState("");
  const save = async () => {
    try {
      const { data } = await api.post("/admin/members/bulk-status", { ids: members.map((m) => m.id), status, reason });
      toast.success(`${data.updated} membre(s) mis à jour${data.skipped.length ? ` · ignorés : ${data.skipped.join(", ")}` : ""}`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="bulk-status-dialog">
        <DialogHeader><DialogTitle>Changer le statut de {members.length} membre(s)</DialogTitle></DialogHeader>
        <select data-testid="bulk-status-select" className="mr-input" value={status} onChange={(e) => setStatus(e.target.value)}>{opts(["actif", "suspendu", "radie"]).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <input data-testid="bulk-status-reason" className="mr-input" placeholder="Motif (journalisé)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <button data-testid="bulk-status-save" className="mr-btn-primary" disabled={reason.trim().length < 3} onClick={save}>Appliquer</button>
      </DialogContent>
    </Dialog>
  );
}

export function Members() {
  const { me } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [bulk, setBulk] = useState(null);
  const status = sp.get("status") || "";
  const load = useCallback(() => api.get("/admin/members", { params: { status, limit: 2000 } }).then(({ data: d }) => setData(d)), [status]);
  useEffect(() => {
    load();
  }, [load]);
  const columns = [
    { key: "name", label: "Nom", className: "font-medium" },
    { key: "phone", label: "Téléphone", value: (m) => fphone(m.phone), className: "tabular whitespace-nowrap" },
    { key: "email", label: "E-mail", hidden: true },
    { key: "company", label: "Entreprise" },
    { key: "sectors", label: "Secteurs", value: (m) => (m.sectors || []).join(", "), hidden: true },
    { key: "sponsor_name", label: "Parrain" },
    { key: "global_role", label: "Rôle", value: (m) => roleLabel(m.global_role) },
    { key: "status", label: "Statut", value: (m) => statusInfo(m.status)[0], render: (m) => <StatusBadge status={m.status} /> },
    { key: "edit", label: "Action", value: () => "", render: (m) => can(me, "members:manage") && <button data-testid="console-member-edit-button" className="mr-btn-ghost min-h-[36px]" onClick={(e) => { e.stopPropagation(); setEdit(m); }}>Rôle</button> },
  ];
  const actions = [
    ...(can(me, "members:manage") ? [{ key: "status", label: "Changer le statut", run: (rows, clear) => setBulk({ rows, clear }) }] : []),
    ...(can(me, "moderation:manage") ? [{ key: "announce", label: "Envoyer une annonce", run: (rows) => nav("/console/annonces", { state: { userIds: rows.map((r) => r.id), names: rows.map((r) => r.name) } }) }] : []),
  ];
  return (
    <div className="mr-fade-up">
      <PageTitle eyebrow="Membres" title="Membres du club">
        <div className="flex gap-2">{can(me, "members:manage") && <button data-testid="new-member-button" className="mr-btn-primary" onClick={() => setCreating(true)}><Plus size={15} /> Nouveau membre</button>}
        <ExportButton path="/admin/members/export" label="CSV" testId="members-export-button" /></div>
      </PageTitle>
      <KpiCards section="members" />
      <div className="flex gap-1 overflow-x-auto border-b border-mr-border mb-4">{MEMBER_TABS.map(([k, l]) => (
        <button key={k} data-testid={`members-tab-${k || "tous"}`} onClick={() => nav(k ? `/console/membres?status=${k}` : "/console/membres")} className={`shrink-0 px-3 min-h-[44px] text-sm border-b-2 -mb-px ${status === k ? "border-mr-accent text-mr-text" : "border-transparent text-mr-muted"}`}>{l}</button>))}</div>
      {!data ? <Loader /> : (
        <DataView page="members" title="Membres" rows={data.items} columns={columns} bulk={actions} onRowClick={(m) => nav(`/console/membres/${m.id}`)}
          filters={[{ key: "global_role", label: "Rôle", options: opts(ROLES, roleLabel) }, { key: "company", label: "Entreprise", options: [...new Set(data.items.map((m) => m.company).filter(Boolean))].sort().map((c) => [c, c]) }]}
          searchKeys={["name", "phone", "email", "company"]} empty="Aucun membre" />
      )}
      {edit && <EditMember m={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {bulk && <BulkStatusDialog members={bulk.rows} onClose={() => setBulk(null)} onDone={() => { bulk.clear(); setBulk(null); load(); }} />}
      {creating && <NewMember onClose={() => setCreating(false)} onSaved={(id) => nav(`/console/membres/${id}`)} />}
    </div>
  );
}

const PAY_STATUSES = ["en_attente_validation", "valide", "rejete", "initie"];

export function PaymentsView({ page, title, params = {}, kanban = false }) {
  const { me } = useAuth();
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/admin/payments", { params }).then(({ data }) => setItems(data)), [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, [load]);
  const decide = async (id, approve) => {
    const reason = approve ? "" : window.prompt("Motif du rejet") || "";
    try {
      await api.post(`/admin/payments/${id}/decision`, { approve, reason });
      toast.success(approve ? "Paiement validé" : "Paiement rejeté");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const bulkValidate = async (rows, clear) => {
    const pending = rows.filter((r) => r.status === "en_attente_validation");
    if (!pending.length) return toast.error("Aucun paiement en attente dans la sélection.");
    if (!window.confirm(`Valider ${pending.length} paiement(s) pour ${fcfa(pending.reduce((s, r) => s + r.amount, 0))} ?`)) return;
    const { data } = await api.post("/admin/payments/bulk-validate", { ids: pending.map((r) => r.id) });
    toast.success(`${data.validated} paiement(s) validé(s)${data.errors.length ? ` · ${data.errors.length} refusé(s) : ${[...new Set(data.errors)].join(" ")}` : ""}`);
    clear();
    load();
  };
  const sum = (rows, st) => fcfa(rows.filter((r) => !st || r.status === st).reduce((s, r) => s + r.amount, 0));
  const columns = [
    { key: "user_name", label: "Membre", className: "font-medium" },
    { key: "created_at", label: "Date", value: (p) => fdate(p.created_at), className: "tabular whitespace-nowrap" },
    { key: "purpose", label: "Objet", value: (p) => purposeLabel(p.purpose) },
    { key: "method", label: "Moyen", value: (p) => methodLabel(p.method), render: (p) => <>{methodLabel(p.method)}{p.cash_flagged && <StatusBadge status="flag" label="Espèces signalées" tone="warning" />}</> },
    { key: "reference", label: "Référence", hidden: true },
    { key: "amount", label: "Montant", value: (p) => fcfa(p.amount), className: "tabular whitespace-nowrap" },
    { key: "status", label: "Statut", value: (p) => statusInfo(p.status)[0], render: (p) => <StatusBadge status={p.status} /> },
    { key: "actions", label: "Décision", value: () => "", render: (p) => p.status === "en_attente_validation" && can(me, "payments:validate") && (
      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}><button data-testid="console-payment-approve-button" className="mr-btn-primary min-h-[36px] px-3" onClick={() => decide(p.id, true)}>Valider</button>
        <button data-testid="console-payment-reject-button" className="mr-btn-danger min-h-[36px] px-3" onClick={() => decide(p.id, false)}>Rejeter</button></div>) },
  ];
  if (!items) return <Loader />;
  return (
    <DataView page={page} title={title} rows={items} columns={columns} empty="Aucun paiement"
      kpis={[{ key: "count", label: "Paiements", value: (r) => r.length }, { key: "pending", label: "À valider", value: (r) => sum(r, "en_attente_validation") },
        { key: "valid", label: "Validés", value: (r) => sum(r, "valide") }, { key: "rejected", label: "Rejetés", value: (r) => r.filter((x) => x.status === "rejete").length }]}
      filters={[...(params.status ? [] : [{ key: "status", label: "Statut", options: opts(PAY_STATUSES) }]), ...(params.purpose ? [] : [{ key: "purpose", label: "Objet", options: opts(["cotisation_annuelle", "frais_service", "billet"], purposeLabel) }]),
        { key: "method", label: "Moyen", options: opts(["virement", "especes", "wave", "orange_money", "mtn_momo", "cinetpay"], methodLabel) }]}
      kanban={kanban ? { get: (p) => p.status, columns: opts(PAY_STATUSES) } : null}
      bulk={can(me, "payments:validate") ? [{ key: "validate", label: "Valider la sélection", run: bulkValidate }] : []} />
  );
}

export function Payments() {
  const [sp] = useSearchParams();
  return (
    <div className="mr-fade-up">
      <PageTitle eyebrow="Cotisations" title="Paiements du club" />
      <PaymentsView page="payments" title="Paiements du club" params={sp.get("status") ? { status: sp.get("status") } : {}} kanban />
    </div>
  );
}

export function Audit() {
  const [data, setData] = useState(null);
  const [check, setCheck] = useState(null);
  const [skip, setSkip] = useState(0);
  useEffect(() => {
    api.get("/admin/audit", { params: { skip } }).then(({ data: d }) => setData(d));
  }, [skip]);
  const verify = async () => setCheck((await api.get("/admin/audit/verify")).data);
  return (
    <div className="mr-fade-up">
      <PageTitle eyebrow="Traçabilité" title="Journal d'audit">
        <button data-testid="audit-verify-button" className="mr-btn-secondary" onClick={verify}>Vérifier l'intégrité</button>
        <ExportButton path="/admin/audit/export" label="CSV complet" testId="audit-export-button" />
      </PageTitle>
      <p className="text-sm text-mr-text2 mb-4">Journal en ajout seul : aucune entrée ne peut être modifiée ou supprimée. Chaque entrée est chaînée à la précédente par une empreinte.</p>
      {check && <p data-testid="audit-verify-result" className={`text-sm mb-4 ${check.valid ? "text-mr-success" : "text-mr-danger"}`}>{check.valid ? `Chaîne intègre : ${check.checked} entrées vérifiées.` : `Rupture détectée à l'entrée n°${check.broken_at_seq}.`}</p>}
      {!data ? <Loader /> : (
        <>
          <DataView page="audit" title="Journal d'audit" rows={data.items} empty="Aucune entrée"
            columns={[{ key: "seq", label: "N°", className: "tabular" }, { key: "created_at", label: "Date", value: (a) => fdatetime(a.created_at), className: "tabular whitespace-nowrap" }, { key: "actor_name", label: "Acteur" },
              { key: "action", label: "Action", className: "font-mono text-xs" }, { key: "object", label: "Objet", value: (a) => `${a.object_type || ""}${a.object_id ? ` · ${/^[0-9a-f]{24}$/.test(a.object_id) ? a.object_id.slice(-6) : a.object_id}` : ""}`, className: "text-mr-text2 text-xs" },
              { key: "ip", label: "IP", className: "tabular text-xs text-mr-muted" }]}
            filters={[{ key: "action", label: "Action", options: [...new Set(data.items.map((a) => a.action))].sort().map((a) => [a, a]) }, { key: "actor_name", label: "Acteur", options: [...new Set(data.items.map((a) => a.actor_name).filter(Boolean))].sort().map((a) => [a, a]) }]} />
          <div className="flex justify-between mt-4 text-sm">
            <button data-testid="audit-prev-button" className="mr-btn-ghost" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - 50))}>Plus récentes</button>
            <span className="text-mr-muted tabular self-center">{data.total} entrées</span>
            <button data-testid="audit-next-button" className="mr-btn-ghost" disabled={skip + 50 >= data.total} onClick={() => setSkip(skip + 50)}>Plus anciennes</button>
          </div>
        </>
      )}
    </div>
  );
}

export function DevLog() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/admin/devlog").then(({ data }) => setItems(data));
  }, []);
  return (
    <div className="mr-fade-up">
      <PageTitle eyebrow="Connecteurs" title="Journal dev (messages sortants)" />
      <p className="text-sm text-mr-text2 mb-4">En mode simulé, les messages WhatsApp, SMS, e-mail et push sont enregistrés ici au lieu d'être envoyés.</p>
      {!items ? <Loader /> : items.length === 0 ? <EmptyState title="Aucun message" testId="devlog-empty" /> : (
        <div className="space-y-2">{items.map((m) => (
          <div key={m.id} className="mr-card p-4" data-testid="devlog-row">
            <div className="flex flex-wrap items-center gap-2 text-xs mb-2"><span className="font-mono uppercase text-mr-accent">{m.channel}</span><span className="tabular text-mr-muted">{m.to}</span><StatusBadge status={m.status} /><span className="ml-auto tabular text-mr-muted">{fdatetime(m.created_at)}</span></div>
            <p className="text-sm font-mono text-mr-text2 break-words">{m.body}</p>
          </div>
        ))}</div>
      )}
    </div>
  );
}
